import { DefaultTheme, useTheme } from "styled-components";
import { BacktestingScoreBreakdown, LanguageNameSpaces } from "@shared/types";
import useRiskChart from "@shared/features/newMarketplace/hooks/useRiskChart";
import { RiskType } from "@shared/features/newMarketplace/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

export const RISK_LEVELS = Object.values(RiskType);

const SHORT_LABELS = {
  [RiskType.LOW]: "risk_level.low",
  [RiskType.MEDIUM]: "risk_level.medium",
  [RiskType.HIGH]: "risk_level.high",
  [RiskType.VERY_HIGH]: "risk_level.very_high",
} as const;

// Risk reads by level: green, amber, orange-red, red.
const HIGH_RISK_COLOR = "#E8672A";
const riskColor = (theme: DefaultTheme, riskType?: RiskType) => {
  const tokens = getNewLookTokens(theme);
  switch (riskType) {
    case RiskType.LOW:
      return tokens.up;
    case RiskType.MEDIUM:
      return tokens.stop;
    case RiskType.HIGH:
      return HIGH_RISK_COLOR;
    case RiskType.VERY_HIGH:
      return tokens.down;
    default:
      return tokens.secondary;
  }
};

/** The channel's risk level (0-3), its meter colour and translated labels. */
const useNewLookRisk = (scoreBreakdown?: BacktestingScoreBreakdown) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const theme = useTheme();
  const { riskType } = useRiskChart({
    scoreBreakdown: scoreBreakdown as BacktestingScoreBreakdown,
  });
  return {
    level: riskType ? RISK_LEVELS.indexOf(riskType) : -1,
    color: riskColor(theme, riskType),
    label: riskType ? t(riskType) : undefined,
    shortLabel: riskType ? t(SHORT_LABELS[riskType]) : undefined,
  };
};

export default useNewLookRisk;
